export interface WebsiteSchema {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[];
}

export interface OrganizationSchema {
  name: string;
  url: string;
  logo: string;
  description: string;
  address?: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint?: {
    telephone: string;
    contactType: string;
    email: string;
  };
  sameAs?: string[];
}

export interface BreadcrumbSchema {
  items: {
    name: string;
    item: string;
  }[];
}

export interface PageSchema {
  title: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
}

export interface ProductSchema {
  name: string;
  description: string;
  image: string;
  url: string;
  brand: {
    name: string;
  };
  offers?: {
    price: number;
    priceCurrency: string;
    availability?: string;
  };
}

export interface ServiceSchema {
  name: string;
  description: string;
  provider: {
    name: string;
    url: string;
  };
  serviceType: string;
  areaServed?: string;
  offers?: {
    price: number;
    priceCurrency: string;
  };
}

// Function to generate Website schema
export const generateWebsiteSchema = (website: WebsiteSchema): string => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": website.name,
    "url": website.url,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${website.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return JSON.stringify(schema);
};

// Function to generate Organization schema
export const generateOrganizationSchema = (organization: OrganizationSchema): string => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": organization.name,
    "url": organization.url,
    "logo": organization.logo,
    "description": organization.description
  };

  if (organization.address) {
    schema.address = {
      "@type": "PostalAddress",
      ...organization.address
    };
  }

  if (organization.contactPoint) {
    schema.contactPoint = {
      "@type": "ContactPoint",
      ...organization.contactPoint
    };
  }

  if (organization.sameAs) {
    schema.sameAs = organization.sameAs;
  }

  return JSON.stringify(schema);
};

// Function to generate BreadcrumbList schema
export const generateBreadcrumbSchema = (breadcrumb: BreadcrumbSchema): string => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumb.items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.item
    }))
  };

  return JSON.stringify(schema);
};

// Function to generate WebPage schema for content pages
export const generatePageSchema = (page: PageSchema): string => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "headline": page.title,
    "description": page.description,
    "url": page.url
  };

  if (page.image) {
    schema.image = page.image;
  }

  if (page.datePublished) {
    schema.datePublished = page.datePublished;
  }

  if (page.dateModified) {
    schema.dateModified = page.dateModified;
  }

  return JSON.stringify(schema);
};

// Function to generate Product schema
export const generateProductSchema = (product: ProductSchema): string => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.image,
    "url": product.url,
    "brand": {
      "@type": "Brand",
      "name": product.brand.name
    }
  };

  if (product.offers) {
    schema.offers = {
      "@type": "Offer",
      ...product.offers
    };
  }

  return JSON.stringify(schema);
};

// Function to generate Service schema
export const generateServiceSchema = (service: ServiceSchema): string => {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": service.provider.name,
      "url": service.provider.url
    },
    "serviceType": service.serviceType
  };

  if (service.areaServed) {
    schema.areaServed = service.areaServed;
  }

  if (service.offers) {
    schema.offers = {
      "@type": "Offer",
      ...service.offers
    };
  }

  return JSON.stringify(schema);
};

// Function to combine multiple schemas
export const combineSchemas = (schemas: string[]): string => {
  const parsedSchemas = schemas.map(schema => JSON.parse(schema));
  
  if (parsedSchemas.length === 1) {
    return schemas[0];
  }
  
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": parsedSchemas.map(schema => {
      const rest = { ...schema };
      delete rest["@context"];
      return rest;
    })
  };
  
  return JSON.stringify(combinedSchema);
};
